import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FolderOpen, Eye } from "lucide-react";
import type { ChangeEntry, DetailData, TreeNode, MoveMapping } from "./TreeDiffViewer";
import {
  BADGE, StatusBadge, badgeColorFor,
  type PanelNode, DiffTreePanelNode,
  injectDeletedGhosts, injectAddedGhosts,
  cloneAsAddedGhost,
  DiffDetailView, type DiffDetailData, TabBtn,
} from "./DiffTreeShared";
import { countChangedBlocks } from "../../utils/markdownDiff";

/* ════════════════════════════════════════════════════════════════════
   Build panel tree from flat TreeNode[] (diff-specific: nodes have slugPath)
   ════════════════════════════════════════════════════════════════════ */

function buildDiffPanelTree(
  nodes: TreeNode[], rootOrder: string[],
  changeMap: Map<string, ChangeEntry>,
): PanelNode[] {
  const childMap = new Map<string, TreeNode[]>();
  for (const n of nodes) {
    if (!n.slugPath || n.slugPath === '__root__') continue;
    const pp = n.slugPath.includes("/") ? n.slugPath.substring(0, n.slugPath.lastIndexOf("/")) : "__root__";
    if (!childMap.has(pp)) childMap.set(pp, []);
    childMap.get(pp)!.push(n);
  }

  const visited = new Set<string>();
  function buildLevel(parentPath: string, order: string[]): PanelNode[] {
    if (visited.has(parentPath)) return [];
    visited.add(parentPath);
    const kids = childMap.get(parentPath) || [];
    const bySlug = new Map<string, TreeNode>();
    for (const k of kids) bySlug.set(k.slug, k);
    const ordered = order.length ? [...order] : [];
    const inOrder = new Set(ordered);
    for (const k of kids) if (!inOrder.has(k.slug)) ordered.push(k.slug);
    const result = ordered.filter(s => bySlug.has(s)).map(slug => {
      const node = bySlug.get(slug)!;
      const sp = node.slugPath;
      const change = changeMap.get(sp);
      const statuses = change ? [...(change.actions || change.changeTypes || [])] : [];
      return {
        slug, title: node.title || slug, slugPath: sp,
        nodeType: node.nodeType || "section", statuses, change,
        children: buildLevel(sp, node.order || []),
      };
    });
    visited.delete(parentPath);
    return result;
  }
  return buildLevel("__root__", rootOrder);
}


/* ════════════════════════════════════════════════════════════════════
   Ghost injection for moved-parent orphans (diff-specific: uses MoveMapping)
   ════════════════════════════════════════════════════════════════════ */

function injectOrphanedDeletedGhosts(
  afterTree: PanelNode[], changeMap: Map<string, ChangeEntry>,
  _changes: ChangeEntry[], moves: MoveMapping[],
): void {
  if (moves.length === 0) return;
  const afterIndex = new Map<string, PanelNode>();
  (function indexAll(nodes: PanelNode[]) {
    for (const n of nodes) { afterIndex.set(n.slugPath, n); indexAll(n.children); }
  })(afterTree);

  for (const move of moves) {
    if (move.deletedChildren.length === 0) continue;
    const parentNode = afterIndex.get(move.newPath);
    if (!parentNode) continue;
    for (const dc of move.deletedChildren) {
      if (parentNode.children.some(ch => ch.slug === dc.slug && ch.isDeletedGhost)) continue;
      const ch = changeMap.get(dc.slugPath);
      // These are leaf-level orphans from MoveMapping — no subtree to clone
      parentNode.children.push({
        slug: dc.slug, title: dc.slug, slugPath: dc.slugPath,
        nodeType: "page", statuses: ["deleted"],
        change: ch || { slugPath: dc.slugPath, actions: ["deleted"] },
        children: [], isDeletedGhost: true,
      });
    }
  }
}

function injectOrphanedAddedGhosts(
  beforeTree: PanelNode[], afterTree: PanelNode[],
  changeMap: Map<string, ChangeEntry>, moves: MoveMapping[],
): void {
  if (moves.length === 0) return;
  const beforeIndex = new Map<string, PanelNode>();
  (function indexAll(nodes: PanelNode[]) {
    for (const n of nodes) { beforeIndex.set(n.slugPath, n); indexAll(n.children); }
  })(beforeTree);
  const afterIndex = new Map<string, PanelNode>();
  (function indexAll(nodes: PanelNode[]) {
    for (const n of nodes) { afterIndex.set(n.slugPath, n); indexAll(n.children); }
  })(afterTree);

  for (const move of moves) {
    const parentInBefore = beforeIndex.get(move.oldPath);
    const parentInAfter = afterIndex.get(move.newPath);
    if (!parentInBefore || !parentInAfter) continue;
    for (const an of parentInAfter.children) {
      const ch = changeMap.get(an.slugPath);
      if (!ch || !(ch.actions || []).includes("added")) continue;
      if (parentInBefore.children.some(c => c.slug === an.slug && c.isAddedGhost)) continue;
      parentInBefore.children.push(cloneAsAddedGhost(an, changeMap));
    }
  }
}

/* ════════════════════════════════════════════════════════════════════
   Changelog row
   ════════════════════════════════════════════════════════════════════ */

function DiffChangeRow({ change, onSelect }: { change: ChangeEntry; onSelect: (c: ChangeEntry) => void }) {
  const actions = change.actions || change.changeTypes || [];
  const primaryAction = actions[0] || "modified";
  const cfg = BADGE[primaryAction] || BADGE.modified;
  const blockCount = actions.includes("modified") ? countChangedBlocks(change.beforeBody || "", change.afterBody || "") : 0;
  return (
    <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-slate-100 hover:bg-slate-50/80 transition-colors cursor-pointer"
      onClick={() => onSelect(change)}>
      <div className={`size-2 rounded-full shrink-0 ${cfg.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-slate-700 truncate">{change.slugPath === "__root__" ? "Content Order" : change.slugPath.split("/").pop()}</div>
        <div className="text-[11px] text-slate-400 font-mono truncate">{change.slugPath === "__root__" ? "Section ordering" : `content/${change.slugPath}`}</div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        {actions.map(a => {
          const b = BADGE[a]; if (!b) return null;
          const label = (a === "modified" && blockCount > 0) ? `${b.label} ${blockCount}` : b.label;
          return <StatusBadge key={a} label={label} color={badgeColorFor(a)} />;
        })}
      </div>
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════════
   Main TwoColumnDiffTree component
   ════════════════════════════════════════════════════════════════════ */

export interface TwoColumnDiffTreeProps {
  beforeNodes: TreeNode[];
  afterNodes: TreeNode[];
  beforeRootOrder: string[];
  afterRootOrder: string[];
  changes: ChangeEntry[];
  moves?: MoveMapping[];
  beforeLabel?: string;
  afterLabel?: string;
  onSelectChange?: (c: ChangeEntry) => void;
  selectedDetail?: DetailData | null;
  detailLoading?: boolean;
  onClearDetail?: () => void;
  conflictPaths?: string[];
}

export default function TwoColumnDiffTree({
  beforeNodes, afterNodes, beforeRootOrder, afterRootOrder, changes,
  moves = [],
  beforeLabel = "Before", afterLabel = "After",
  onSelectChange, selectedDetail, detailLoading, onClearDetail,
  conflictPaths,
}: TwoColumnDiffTreeProps) {
  type Tab = "tree" | "changelog";
  const [tab, setTab] = useState<Tab>("tree");
  const [expandMap, setExpandMap] = useState<Record<string, boolean>>({});
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const beforePanelRef = useRef<HTMLDivElement>(null);
  const afterPanelRef = useRef<HTMLDivElement>(null);

  /* Sync scroll across both panels */
  const isSyncingScroll = useRef(false);
  useEffect(() => {
    const panels = [beforePanelRef.current, afterPanelRef.current].filter(Boolean) as HTMLDivElement[];
    if (panels.length < 2) return;
    function handleScroll(e: Event) {
      if (isSyncingScroll.current) return;
      isSyncingScroll.current = true;
      const source = e.target as HTMLDivElement;
      for (const p of panels) { if (p !== source) p.scrollTop = source.scrollTop; }
      isSyncingScroll.current = false;
    }
    for (const p of panels) p.addEventListener("scroll", handleScroll);
    return () => { for (const p of panels) p.removeEventListener("scroll", handleScroll); };
  });

  /* Build change map */
  const changeMap = useMemo(() => {
    const m = new Map<string, ChangeEntry>();
    for (const c of changes) {
      m.set(c.slugPath, c);
      if (c.originalSlugPath) m.set(c.originalSlugPath, c);
    }
    return m;
  }, [changes]);

  const conflictPathsSet = useMemo(() => new Set(conflictPaths || []), [conflictPaths]);

  /* Build independent panel trees */
  const beforeTree = useMemo(() => buildDiffPanelTree(beforeNodes, beforeRootOrder, changeMap), [beforeNodes, beforeRootOrder, changeMap]);
  const afterTree = useMemo(() => buildDiffPanelTree(afterNodes, afterRootOrder, changeMap), [afterNodes, afterRootOrder, changeMap]);

  /* Inject ghosts */
  const afterTreeWithGhosts = useMemo(() => {
    const result = injectDeletedGhosts(afterTree, beforeTree, changeMap);
    if (moves.length > 0) injectOrphanedDeletedGhosts(result, changeMap, changes, moves);
    return result;
  }, [afterTree, beforeTree, changeMap, moves, changes]);

  const beforeTreeWithGhosts = useMemo(() => {
    const result = injectAddedGhosts(beforeTree, afterTree, changeMap);
    if (moves.length > 0) injectOrphanedAddedGhosts(result, afterTree, changeMap, moves);
    return result;
  }, [beforeTree, afterTree, changeMap, moves]);

  /* Use trees directly — no sync ghost alignment */
  const syncedBefore = beforeTreeWithGhosts;
  const syncedAfter = afterTreeWithGhosts;

  /* Move path remap from backend moves data */
  const remapPath = useMemo(() => {
    const moveRemap: [string, string][] = [];
    for (const m of moves) {
      moveRemap.push([m.oldPath, m.newPath]);
      for (const ch of m.children) moveRemap.push([ch.oldPath, ch.newPath]);
    }
    return (path: string): string | null => {
      for (const [oldPfx, newPfx] of moveRemap) {
        if (path === oldPfx) return newPfx;
        if (path.startsWith(oldPfx + "/")) return newPfx + path.slice(oldPfx.length);
      }
      return null;
    };
  }, [moves]);

  const movedOriginToNew = useMemo(() => {
    const m = new Map<string, string>();
    for (const mv of moves) {
      m.set(mv.oldPath, mv.newPath);
      for (const ch of mv.children) m.set(ch.oldPath, ch.newPath);
    }
    return m;
  }, [moves]);

  const movedNewToOrigin = useMemo(() => {
    const m = new Map<string, string>();
    for (const mv of moves) {
      m.set(mv.newPath, mv.oldPath);
      for (const ch of mv.children) m.set(ch.newPath, ch.oldPath);
    }
    return m;
  }, [moves]);

  /* Auto-expand all ancestors of changed nodes + sync ghosts */
  useEffect(() => {
    const map: Record<string, boolean> = {};
    // Collect all changed node slugPaths
    const changedPaths = new Set<string>();
    function collectChanged(nodes: PanelNode[]) {
      for (const n of nodes) {
        if (n.statuses.length > 0 || n.isSyncGhost) changedPaths.add(n.slugPath);
        collectChanged(n.children);
      }
    }
    collectChanged(syncedBefore);
    collectChanged(syncedAfter);
    // Expand every ancestor of every changed node
    for (const sp of changedPaths) {
      const parts = sp.split("/");
      for (let i = 1; i < parts.length; i++) {
        const ancestor = parts.slice(0, i).join("/");
        map[ancestor] = true;
        // Also expand moved counterparts
        if (movedOriginToNew.has(ancestor)) map[movedOriginToNew.get(ancestor)!] = true;
        if (movedNewToOrigin.has(ancestor)) map[movedNewToOrigin.get(ancestor)!] = true;
        const remapped = remapPath(ancestor);
        if (remapped) map[remapped] = true;
      }
      // If the changed node itself has children (e.g. ORD), expand it too
      map[sp] = true;
      if (movedOriginToNew.has(sp)) map[movedOriginToNew.get(sp)!] = true;
      if (movedNewToOrigin.has(sp)) map[movedNewToOrigin.get(sp)!] = true;
      const remapped = remapPath(sp);
      if (remapped) map[remapped] = true;
    }
    setExpandMap(map);
  }, [syncedBefore, syncedAfter, movedOriginToNew, movedNewToOrigin, remapPath]);

  const changeCount = changes.length;

  /* Toggle expand — also toggle alternate path for moved nodes */
  const onToggle = useCallback((sp: string) => {
    setExpandMap(prev => {
      const val = !prev[sp];
      const next = { ...prev, [sp]: val };
      if (movedOriginToNew.has(sp)) next[movedOriginToNew.get(sp)!] = val;
      if (movedNewToOrigin.has(sp)) next[movedNewToOrigin.get(sp)!] = val;
      const remapped = remapPath(sp);
      if (remapped) next[remapped] = val;
      return next;
    });
  }, [movedOriginToNew, movedNewToOrigin, remapPath]);

  /* Eye icon for previewable nodes — fixed-width slot for alignment */
  const iconBtn = "p-1 rounded border border-slate-200 transition-colors cursor-pointer";
  const spacer = <span className="inline-block w-[26px] shrink-0" />;
  const actionWidth = "w-[26px]"; // 1 slot: eye only

  const renderTrailingExtra = useCallback((node: PanelNode) => {
    const ch = changeMap.get(node.slugPath);
    if (!ch) return <span className={`flex items-center justify-end gap-0.5 shrink-0 ${actionWidth}`} />;
    const actions = ch.actions || ch.changeTypes || [];
    const canPreview = actions.some((a: string) => a === "modified" || a === "added" || a === "deleted" || a === "reordered");
    if (!canPreview) return <span className={`flex items-center justify-end gap-0.5 shrink-0 ${actionWidth}`}>{spacer}</span>;
    return (
      <span className={`flex items-center justify-end gap-0.5 shrink-0 ${actionWidth}`}>
        <button onClick={e => { e.stopPropagation(); if (onSelectChange) onSelectChange(ch); }}
          className={`${iconBtn} text-slate-400 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50`} title="Preview">
          <Eye className="size-3.5" />
        </button>
      </span>
    );
  }, [changeMap, onSelectChange]);

  /* Change count for MOD badges */
  const changeCountFn = useCallback((n: PanelNode) => {
    const ch = changeMap.get(n.slugPath) || n.change;
    if (!ch) return null;
    const actions: string[] = ch.actions || ch.changeTypes || n.statuses || [];
    if (!actions.includes("modified")) return null;
    const orig = ch.beforeBody;
    const curr = ch.afterBody;
    if (!orig && !curr) return null;
    return countChangedBlocks(orig || "", curr || "");
  }, [changeMap]);

  /* Hide ORD badges on the before (committed) panel — reorder is a working-state change */
  const hideOrdBadge = useCallback((node: PanelNode) => {
    return node.statuses.length > 0 && !node.statuses.every(s => s === "reordered");
  }, []);

  /* Select node + cross-panel scroll (row click only highlights, preview is via eye button) */
  const onNodeSelect = useCallback((sp: string) => {
    setSelectedSlug(prev => prev === sp ? null : sp);

    const altPaths: string[] = [];
    if (movedOriginToNew.has(sp)) altPaths.push(movedOriginToNew.get(sp)!);
    if (movedNewToOrigin.has(sp)) altPaths.push(movedNewToOrigin.get(sp)!);
    const remapped = remapPath(sp);
    if (remapped && !altPaths.includes(remapped)) altPaths.push(remapped);

    const allPaths = [sp, ...altPaths];
    setExpandMap(prev => {
      const next = { ...prev };
      for (const path of allPaths) {
        const parts = path.split("/");
        for (let i = 1; i < parts.length; i++) next[parts.slice(0, i).join("/")] = true;
      }
      return next;
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        for (const ref of [beforePanelRef, afterPanelRef]) {
          if (!ref.current) continue;
          let el = ref.current.querySelector(`[data-slugpath="${CSS.escape(sp)}"]`);
          if (!el) {
            for (const alt of altPaths) {
              el = ref.current.querySelector(`[data-slugpath="${CSS.escape(alt)}"]`);
              if (el) break;
            }
          }
          if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
    });
  }, [movedOriginToNew, movedNewToOrigin, remapPath, changeMap, onSelectChange]);

  /* Detail view takes over — full-screen overlay for maximum width */
  if (selectedDetail && onClearDetail) {
    const detailData: DiffDetailData = {
      slugPath: selectedDetail.slugPath,
      title: selectedDetail.title,
      actions: selectedDetail.change?.actions || selectedDetail.change?.changeTypes || [],
      beforeBody: selectedDetail.beforeBody,
      afterBody: selectedDetail.afterBody,
      beforeTitle: selectedDetail.beforeTitle,
      afterTitle: selectedDetail.afterTitle,
      originalSlugPath: selectedDetail.change?.originalSlugPath,
      nodeType: selectedDetail.nodeType,
      changedAssets: selectedDetail.changedAssets,
      beforeSpec: selectedDetail.beforeSpec,
      afterSpec: selectedDetail.afterSpec,
      beforeMeta: selectedDetail.beforeMeta,
      afterMeta: selectedDetail.afterMeta,
      sectionType: selectedDetail.sectionType,
    };
    const allDetailData: DiffDetailData[] = changes.map(c => ({
      slugPath: c.slugPath,
      title: c.slugPath.split("/").pop() || c.slugPath,
      actions: c.actions || c.changeTypes || [],
      beforeBody: "", afterBody: "",
    }));
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[2px]">
        <div className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden" style={{ width: "94vw", height: "94vh" }}>
          <DiffDetailView
            detail={detailData}
            onBack={onClearDetail}
            allChanges={allDetailData}
            onNavigate={onSelectChange ? (d) => {
              const ch = changes.find(c => c.slugPath === d.slugPath);
              if (ch) onSelectChange(ch);
            } : undefined}
            loading={detailLoading}
            beforeLabel={beforeLabel}
            afterLabel={afterLabel}
          />
        </div>
      </div>
    );
  }

  const noChanges = beforeTree.length === 0 && afterTree.length === 0 && changes.length === 0;
  if (noChanges) {
    return <div className="py-8 text-center text-[14px] text-slate-400">No changes.</div>;
  }

  /* Root "Content" node wrapper */
  const renderRootContent = (tree: PanelNode[], panelRef: React.RefObject<HTMLDivElement | null>, side?: "before" | "after") => (
    <div ref={panelRef} className="flex-1 overflow-auto scrollbar-mac">
      <div
        className={`flex items-center gap-1.5 px-2 text-[13px] text-slate-900 cursor-pointer h-[32px] ${selectedSlug === "__root_content__" ? "ring-1 ring-inset ring-indigo-400 bg-indigo-50/30" : ""}`}
        data-slugpath="__root_content__"
        onClick={() => onNodeSelect("__root_content__")}
      >
        <FolderOpen className="size-3.5 text-amber-500 shrink-0 ml-1" />
        <span className="font-medium">Content</span>
      </div>
      {tree.map(n => (
        <DiffTreePanelNode
          key={n.slugPath} node={n} depth={1}
          expandMap={expandMap} onToggle={onToggle}
          selectedSlug={selectedSlug} onNodeSelect={onNodeSelect}
          showBadge={side === "before" ? hideOrdBadge : undefined}
          renderTrailingExtra={renderTrailingExtra}
          changeCountFn={changeCountFn}
          conflictPaths={conflictPathsSet}
        />
      ))}
    </div>
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Tabs */}
      <div className="flex items-center justify-between px-5 py-2 border-b border-slate-100 shrink-0 bg-slate-50/50">
        <div className="flex items-center gap-1 p-0.5 rounded-lg bg-slate-100">
          <TabBtn active={tab === "tree"} onClick={() => setTab("tree")}>Tree</TabBtn>
          <TabBtn active={tab === "changelog"} onClick={() => setTab("changelog")}>List</TabBtn>
        </div>
        <span className="text-[12px] text-slate-400">{changeCount} change{changeCount !== 1 ? "s" : ""}</span>
      </div>

      {tab === "tree" ? (
        <>
          {/* Column headers */}
          <div className="grid grid-cols-2 border-b border-slate-200 shrink-0">
            <div className="px-3 py-1.5 bg-indigo-50 flex items-center gap-1.5 border-r border-slate-200">
              <div className="size-2 rounded-full bg-indigo-400" />
              <span className="text-[12px] font-semibold text-indigo-700">{beforeLabel}</span>
            </div>
            <div className="px-3 py-1.5 bg-orange-100 flex items-center gap-1.5">
              <div className="size-2 rounded-full bg-orange-400" />
              <span className="text-[12px] font-semibold text-orange-700">{afterLabel}</span>
              {changeCount > 0 && (
                <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-orange-200/60 text-orange-600 font-medium">{changeCount}</span>
              )}
            </div>
          </div>

          {/* Two-column tree panels */}
          <div className="flex-1 grid grid-cols-2 min-h-0 overflow-hidden">
            <div className="border-r border-slate-200 overflow-hidden flex flex-col">
              {renderRootContent(syncedBefore, beforePanelRef, "before")}
            </div>
            <div className="overflow-hidden flex flex-col">
              {renderRootContent(syncedAfter, afterPanelRef, "after")}
            </div>
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-auto scrollbar-mac px-5 py-4 space-y-1">
          {changes.length === 0 ? (
            <div className="py-8 text-center text-[14px] text-slate-400">No changes to show.</div>
          ) : (
            changes.map(c => (
              <DiffChangeRow key={c.slugPath} change={c} onSelect={onSelectChange || (() => {})} />
            ))
          )}
        </div>
      )}
    </div>
  );
}
